import { Navigate, Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Stack } from '@vipso/shared';
import { useAccess } from '../hooks/useAccess';
import {
  useModuleEntitled,
  type TerminologyModule,
} from '../hooks/useModuleLabel';

type RequireFeatureProps = {
  /** All of these keys must be present. */
  allOf?: string[];
  /** At least one of these keys must be present. */
  anyOf?: string[];
  /** Customer/vendor/product: use module entitlement (org feature + keys). */
  entitledModule?: TerminologyModule;
  redirectTo?: string;
};

function useAllowed(
  allOf?: string[],
  anyOf?: string[],
  entitledModule?: TerminologyModule,
): boolean {
  const { can } = useAccess();
  // Hooks must be unconditional — call for a fixed module and ignore when unused.
  const customerOk = useModuleEntitled('customer');
  const vendorOk = useModuleEntitled('vendor');
  const productOk = useModuleEntitled('product');

  if (entitledModule) {
    if (entitledModule === 'customer') return customerOk;
    if (entitledModule === 'vendor') return vendorOk;
    if (entitledModule === 'product') return productOk;
  }
  if (allOf?.length) {
    if (!allOf.every((k) => can(k))) return false;
  }
  if (anyOf?.length) {
    if (!anyOf.some((k) => can(k))) return false;
  }
  if (!allOf?.length && !anyOf?.length && !entitledModule) return false;
  return true;
}

/** Route guard: redirect when the user lacks required feature keys. */
export function RequireFeature({
  allOf,
  anyOf,
  entitledModule,
  redirectTo = '/dashboard',
}: RequireFeatureProps) {
  const allowed = useAllowed(allOf, anyOf, entitledModule);
  if (!allowed) return <Navigate to={redirectTo} replace />;
  return <Outlet />;
}

/** Inline no-access empty state for tab pages with zero allowed modules. */
export function NoAccessState({ onBack }: { onBack?: () => void }) {
  const { t } = useTranslation();
  return (
    <Stack align="flex-start" gap="md" py="xl">
      <Alert color="yellow" title={t('common.noAccess')}>
        {t('common.noAccessDescription')}
      </Alert>
      {onBack ? (
        <Button variant="light" onClick={onBack}>
          {t('common.back')}
        </Button>
      ) : null}
    </Stack>
  );
}
